import React, { useState, useEffect } from 'react';
import { XMarkIcon, MagnifyingGlassIcon, UserIcon } from '@heroicons/react/24/outline';
import { noteApi } from '../services/noteApi';
import { Note, LeadOption } from '../types/Note';

interface NotesFormProps {
  note?: Note | null;
  preSelectedLead?: LeadOption;
  onClose: () => void;
  onSuccess: () => void;
}

const NotesForm: React.FC<NotesFormProps> = ({ note, preSelectedLead, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    leadId: undefined as number | undefined,
    content: ''
  });
  const [selectedLead, setSelectedLead] = useState<LeadOption | null>(null);
  const [leads, setLeads] = useState<LeadOption[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showLeadDropdown, setShowLeadDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (note) {
      setFormData({
        leadId: note.leadId || undefined,
        content: note.content
      });
      if (note.leadId && note.leadName) {
        setSelectedLead({
          id: note.leadId,
          name: note.leadName,
          email: note.leadEmail || '',
          phone: ''
        });
        setSearchTerm(note.leadName);
      }
    } else if (preSelectedLead) {
      setFormData({
        leadId: preSelectedLead.id,
        content: ''
      });
      setSelectedLead(preSelectedLead);
      setSearchTerm(preSelectedLead.name);
    }
    fetchLeads();
  }, [note, preSelectedLead]);

  const fetchLeads = async (search: string = '') => {
    try {
      const response = await noteApi.getLeads(search);
      if (response.success) {
        setLeads(response.data);
      }
    } catch (err) {
      console.error('Error fetching leads:', err);
    }
  };

  const handleLeadSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    fetchLeads(value);
    setShowLeadDropdown(true);
  };

  const handleLeadSelect = (lead: LeadOption) => {
    setSelectedLead(lead);
    setFormData(prev => ({ ...prev, leadId: lead.id }));
    setSearchTerm(lead.name);
    setShowLeadDropdown(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.content.trim()) {
      setError('Please enter note content');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (note) {
        // Update existing note
        const response = await noteApi.updateNote(note.id, formData);
        if (response.success) {
          onSuccess();
        } else {
          setError(response.message || 'Failed to update note');
        }
      } else {
        // Create new note - only include leadId if it's defined
        const noteData = formData.leadId ? formData : { content: formData.content };
        const response = await noteApi.createNote(noteData);
        if (response.success) {
          onSuccess();
        } else {
          setError(response.message || 'Failed to create note');
        }
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const filteredLeads = leads.filter(lead =>
    lead.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    lead.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {note ? 'Edit Note' : 'Add New Note'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          {/* Lead Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Lead (Optional)
            </label>
            <div className="relative">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search for a lead..."
                  value={searchTerm}
                  onChange={handleLeadSearch}
                  onFocus={() => !preSelectedLead && setShowLeadDropdown(true)}
                  disabled={!!preSelectedLead}
                  className={`w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    preSelectedLead ? 'bg-gray-100 cursor-not-allowed' : ''
                  }`}
                />
              </div>
              
              {showLeadDropdown && !preSelectedLead && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {filteredLeads.length === 0 ? (
                    <div className="px-4 py-3 text-gray-500 text-sm">
                      No leads found
                    </div>
                  ) : (
                    filteredLeads.map((lead) => (
                      <button
                        key={lead.id}
                        type="button"
                        onClick={() => handleLeadSelect(lead)}
                        className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center space-x-3"
                      >
                        <UserIcon className="h-5 w-5 text-gray-400" />
                        <div>
                          <div className="font-medium text-gray-900">{lead.name}</div>
                          <div className="text-sm text-gray-500">{lead.email}</div>
                        </div>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
            
            {selectedLead && !preSelectedLead && (
              <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <UserIcon className="h-4 w-4 text-blue-600" />
                    <span className="text-sm font-medium text-blue-900">
                      Selected: {selectedLead.name}
                    </span>
                    <span className="text-sm text-blue-700">({selectedLead.email})</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedLead(null);
                      setFormData(prev => ({ ...prev, leadId: undefined }));
                      setSearchTerm('');
                    }}
                    className="text-sm text-blue-700 hover:text-blue-900 underline"
                  >
                    Clear
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Note Content */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Note Content *
            </label>
            <textarea
              name="content"
              value={formData.content}
              onChange={handleInputChange}
              placeholder="Enter your note here..."
              rows={6}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              required
            />
            <div className="mt-1 text-sm text-gray-500">
              {formData.content.length} characters
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !formData.content.trim()}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Saving...' : (note ? 'Update Note' : 'Create Note')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NotesForm;
